import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, DollarSign, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentsPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [accruals, setAccruals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    accrual_id: '',
    amount: '',
    payment_method: 'cash',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, tenantsRes, accrualsRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tenants`, { withCredentials: true }),
        axios.get(`${API_URL}/api/accruals`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data);
      setTenants(tenantsRes.data);
      setAccruals(accrualsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        accrual_id: formData.accrual_id || null
      };

      await axios.post(`${API_URL}/api/payments`, payload, { withCredentials: true });
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      accrual_id: '',
      amount: '',
      payment_method: 'cash',
      notes: ''
    });
  };

  const getTenantName = (tenantId) => tenants.find(t => t.id === tenantId)?.name || '-';

  const getTenantAccruals = (tenantId) => {
    return accruals.filter(a => a.tenant_id === tenantId && a.remaining_amount > 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('payments')}</h1>
          <p className="text-gray-500 text-sm mt-1">{payments.length} {t('total')}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-payment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addPayment')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('totalReceived')}</p>
          <p className="font-heading text-3xl font-bold text-emerald-600">
            {currency}{payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('thisMonth')}</p>
          <p className="font-heading text-3xl font-bold">
            {currency}{payments.filter(p => {
              const paymentDate = new Date(p.payment_date);
              const now = new Date();
              return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
            }).reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('totalPayments')}</p>
          <p className="font-heading text-3xl font-bold">{payments.length}</p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('paymentDate')}</th>
              <th>{t('tenants')}</th>
              <th>{t('amount')}</th>
              <th>{t('paymentMethod')}</th>
              <th>{t('notes')}</th>
            </tr>
          </thead>
          <tbody>
            {payments.length > 0 ? (
              payments.map((payment) => (
                <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                  <td>{payment.payment_date?.split('T')[0]}</td>
                  <td className="font-medium">{getTenantName(payment.tenant_id)}</td>
                  <td className="font-mono text-emerald-600">+{currency}{payment.amount}</td>
                  <td className="uppercase text-xs">{t(payment.payment_method) || payment.payment_method}</td>
                  <td className="text-gray-500">{payment.notes || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {t('addPayment')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('tenants')} *</label>
              <Select 
                value={formData.tenant_id} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, tenant_id: val, accrual_id: '' }))}
              >
                <SelectTrigger className="rounded-none mt-1" data-testid="payment-tenant-select">
                  <SelectValue placeholder={t('selectTenant')} />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.tenant_id && getTenantAccruals(formData.tenant_id).length > 0 && (
              <div>
                <label className="text-sm font-medium">{t('linkToAccrual')} ({t('optional')})</label>
                <Select 
                  value={formData.accrual_id} 
                  onValueChange={(val) => {
                    const accrual = accruals.find(a => a.id === val);
                    setFormData(prev => ({ 
                      ...prev, 
                      accrual_id: val,
                      amount: accrual ? accrual.remaining_amount.toString() : prev.amount
                    }));
                  }}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue placeholder={t('selectAccrual')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getTenantAccruals(formData.tenant_id).map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.accrual_type === 'rent' ? (language === 'tr' ? 'Kira' : 'Rent') : a.accrual_type === 'deposit' ? (language === 'tr' ? 'Depozito' : 'Deposit') : a.accrual_type} - {currency}{a.remaining_amount} ({a.due_date?.split('T')[0]})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('amount')} *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('paymentMethod')} *</label>
                <Select 
                  value={formData.payment_method} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, payment_method: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                    <SelectItem value="bank_transfer">{t('bankTransfer')}</SelectItem>
                    <SelectItem value="card">{t('card')}</SelectItem>
                    <SelectItem value="manual">{t('manual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('notes')}</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="rounded-none mt-1"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="payment-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
